import React, { createRef, useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { TabContext } from '@mui/lab'
import { Tab, Tabs, Tooltip, Typography } from '@mui/material'

import { flowConditionTabNames } from '../../../../../../constants'
import { Logger, LoggerTypes } from '../../../../../../utils'
import { Accordion, AccordionDetails, AccordionSummary, Icon, iconNames, TabPanel } from '../../../../../../components'
import { FlowElementConditionAboutPanel } from '../FlowElementConditionAboutPanel'
import { FlowElementConditionCheckPanel } from '../FlowElementConditionCheckPanel'
import '../../../common-elements-style.scss'
import './style.scss'

/**
 * @returns {div}
 */
export const FlowElementConditionTab = ({
  currentProject,
  currentScenario,
  currentFlowGroup,
  currentElement,
  // -- Scenario's attributes
  resources,
  variables,
  parameters,
  // -- Flow's Actions
  conditions,
  // -- refresh after saving
  refreshAfterSave
}) => {
  const [tabValue, setTabValue] = useState(0)
  const { t } = useTranslation()
  const ref = createRef()

  // -- This variable is used to temporary locally save the changing condition data between accordions
  // -- to be able to save in one place even if changes have been made in different accordion's forms.
  // const [currentCondition, setCurrentCondition] = useState(null)
  const [conditionTemp, setConditionTemp] = useState(null)
  const [accordionExpanded, setAccordionExpanded] = useState(flowConditionTabNames.ABOUT)

  const flowGroups = useSelector((state) => state.flowGroups)
  const flowGroupConditions = flowGroups?.flowGroupConditions.get(currentFlowGroup.id) || null
  const currentCondition = flowGroupConditions?.get(currentElement.id) || null

  // -- Find the flow element action from the flowGroup data.
  // let _currentCondition = []
  // ;(currentFlowGroup?.flows || [])
  //   .map((flow) => (flow?.conditions || []).map((el) => (el?.element_id === currentElement.id ? el : null)))
  //   .filter((el) => el && el !== null && typeof el !== 'undefined' && el.length > 0)
  //   .forEach((el) => {
  //     if (el && typeof el === 'object') {
  //       el.forEach((_el) => {
  //         if (_el) {
  //           _currentCondition.push(_el)
  //         }
  //       })
  //     }
  //   })
  // const __currentCondition = _currentCondition.find((el) => el && el.id) || null
  useEffect(() => {
    Logger.trace(LoggerTypes.CONTROLLER, 'FlowElementConditionTab.useEffect(...)', {
      flowGroups,
      currentFlowGroup,
      currentCondition,
      currentElementId: currentElement.id
    })
    // setConditionTemp(currentCondition)
  }, [currentElement, currentFlowGroup])

  // Each time something is updated by the user in any tab or accordion, we refresh the conditionTemp
  // which is used to display the current (non-submitted) values of the current selected BPMN element
  const _getConditionTemp = () => {
    return {
      ...conditionTemp,
      checks: [...(currentCondition?.checks || [])]
    }
  }

  const refreshConditionTemp = () => {
    setConditionTemp(_getConditionTemp())
  }

  const onTabChange = (e, newTabValue) => {
    Logger.trace(LoggerTypes.CONTROLLER, 'FlowElementConditionTab.onTabChange()', { e, newTabValue })
    setTabValue(newTabValue)
    refreshConditionTemp()
  }

  const onChangeAccordion = (panel) => (e, newExpanded) => {
    setAccordionExpanded(newExpanded ? panel : false)
    // setCurrentCondition(currentCondition)
    refreshConditionTemp()
    Logger.trace(LoggerTypes.CONTROLLER, 'FlowElementConditionTab.onChangeAccordion()', {
      newExpanded,
      currentCondition
    })
  }

  Logger.trace(LoggerTypes.CONTAINER, 'FlowElementConditionTab.render()', {
    tabValue,
    currentElement,
    currentProject,
    currentScenario,
    currentFlowGroup,
    currentCondition,
    // -- Scenario's attributes
    resources,
    variables,
    parameters,
    // -- Flow's actions
    conditions
  })

  return currentCondition ? (
    <div className="FlowElementConditionTab">
      <TabContext value={tabValue}>
        <Tabs value={tabValue} onChange={onTabChange}>
          <Tooltip ref={ref} title={t('flowActions.conditions.tabs.about')}>
            <Tab disableRipple={true} id="simple-tab-0" icon={<Icon name={iconNames.ABOUT} size={20} />} />
          </Tooltip>

          <Tooltip ref={ref} title={t('flowActions.conditions.tabs.check')}>
            <Tab disableRipple={true} id="simple-tab-1" icon={<Icon name={iconNames.CHECK} size={20} />} />
          </Tooltip>
        </Tabs>
        <TabPanel value={tabValue} index={0}>
          {/*  -- ABOUT --  */}
          <Accordion
            expanded={accordionExpanded === flowConditionTabNames.ABOUT}
            onChange={onChangeAccordion(flowConditionTabNames.ABOUT)}
          >
            <AccordionSummary
              aria-controls={`${flowConditionTabNames.ABOUT}-content`}
              id={`${flowConditionTabNames.ABOUT}-header`}
            >
              <Typography>
                <Icon name={iconNames.ABOUT} size={12} />
                <span className="iconAccordion">{t('flowActions.conditions.panels.about.title')}</span>
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <FlowElementConditionAboutPanel
                currentProject={currentProject}
                currentScenario={currentScenario}
                currentFlowGroup={currentFlowGroup}
                currentCondition={currentCondition}
                refreshAfterSave={refreshAfterSave}
              />
            </AccordionDetails>
          </Accordion>
        </TabPanel>
        <TabPanel value={tabValue} index={1}>
          {/*  -- CHECK_ON_START --  */}
          <Accordion
            expanded={accordionExpanded === flowConditionTabNames.CHECK_ON_START}
            onChange={onChangeAccordion(flowConditionTabNames.CHECK_ON_START)}
          >
            <AccordionSummary
              aria-controls={`${flowConditionTabNames.CHECK_ON_START}-content`}
              id={`${flowConditionTabNames.CHECK_ON_START}-header`}
            >
              <Typography>
                <Icon name={iconNames.FLAG_START} size={12} />
                <span className="iconAccordion">{t('flowActions.conditions.panels.check_on_start.title')}</span>
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <FlowElementConditionCheckPanel
                currentProject={currentProject}
                currentScenario={currentScenario}
                currentFlowGroup={currentFlowGroup}
                currentCondition={currentCondition}
                resources={resources}
                variables={variables}
                parameters={parameters}
                // refreshAfterSaving={refreshAfterSaving}
              />
            </AccordionDetails>
          </Accordion>
        </TabPanel>
      </TabContext>
    </div>
  ) : (
    <div></div>
  )
}

FlowElementConditionTab.displayName = 'FlowElementConditionTab'

FlowElementConditionTab.propTypes = {
  currentProject: PropTypes.object.isRequired,
  currentScenario: PropTypes.object.isRequired,
  currentFlowGroup: PropTypes.object.isRequired,
  currentElement: PropTypes.object,
  // -- Scenario's attributes
  variables: PropTypes.arrayOf(PropTypes.object),
  resources: PropTypes.arrayOf(PropTypes.object),
  parameters: PropTypes.arrayOf(PropTypes.object),
  // -- Flow's Action
  conditions: PropTypes.arrayOf(PropTypes.object),
  // -- refresh after saving
  refreshAfterSave: PropTypes.func
}

FlowElementConditionTab.defaultProps = {
  currentProject: null,
  currentScenario: null,
  currentFlowGroup: null,
  currentElement: null,
  // -- Scenario's attributes
  variables: [],
  resources: [],
  parameters: [],
  // -- Flow's Action
  conditions: [],
  // -- refresh after saving
  refreshAfterSave: null
}
